import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, empty, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { CartItem, CheckOutDetail, ICartItem, ICheckOutProcessModel, BaseResponse } from '../models';
import { ApiService } from './api.service';
import { ConfigService } from './config.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  constructor(private httpClient: HttpClient, private userService: UserService) { }

  public totalCartItem: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  //#region CartService Api endPoints
  public getItems1() {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.GET_ITEMS;
    const data = {
      customerNumber: this.userService.readUserObject.customerNumber
    };
    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, data).pipe(
      catchError((err, caught) => { console.log(err); console.log(caught); return empty(); }),
      map((response: BaseResponse) => response.data)
    );
  }

  public getItems(customerNumber: string): Observable<CartItem[]> {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.GET_ITEMS;
    const data = {
      customerNumber: customerNumber
    };
    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, data).pipe(
      catchError((err, caught) => { console.log(err); console.log(caught); return empty(); }),
      map((response: BaseResponse) => response.data)
    );
  }

  public removeItem(cartId: string) {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.REMOVE_ITEM;
    const data = { cartId };
    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, data) as Observable<any>;
  }

  public emptyCart() {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.EMPTY_CART;
    const data = {
      customerNumber: this.userService.readUserObject.customerNumber
    };
    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, data) as Observable<any>;
  }

  public updateCart(item: ICartItem): Observable<any> {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.UPDATE_ITEM;
    const data = [];
    item.customerNumber = this.userService.readUserObject.customerNumber
    data.push(item);
    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, data) as Observable<any>;
  }

  public addStockItem(item: ICartItem): Observable<any> {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.ADD_STOCK_ITEM;
    const data = [];
    item.customerNumber = this.userService.readUserObject.customerNumber
    data.push(item);
    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, data).pipe(
      catchError((err, caught) => { return throwError(err); }),
      map((response: BaseResponse) => response.data)
    ) as Observable<any>;
  }

  public addConfiguredItemToCart(data: any): Observable<any> {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.ADD_CONFIGURED_ITEM_TO_CART;
    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, data) as Observable<any>;
  }

  public uploadItems(data: ICartItem[]): Observable<any> {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.UPLOAD_ITEMS;
    data.forEach(el => {
      el.customerNumber = this.userService.readUserObject.customerNumber
    })
    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, data) as Observable<any>;
  }

  public getItemCountInCart(): Observable<number> {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.GET_CART_ITEM_COUNT;
    const data = {
      customerNumber: this.userService.readUserObject.customerNumber
    }
    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, data)
      .pipe(
        catchError((err, caught) => { console.log(err); console.log(caught); return empty(); }),
        map((response: BaseResponse) => { let result: any; result = response.data; return result; }),
        tap((totalCartItem) => { this.totalCartItem.next(totalCartItem) }));;
  }

  public getCartDiscount() {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.GET_CART_DISCOUNT;
    return ApiService.genericObservableGet(this.httpClient, apiEndpoint)
      .pipe(
        catchError((err, caught) => { console.log(err); console.log(caught); return empty(); }),
        map((response: BaseResponse) => response.data),
      ) as Observable<any>;
  }
  public GetCartPromoCodeFee(customerNumber: string, promoCode, cartItemsTotal:string) {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.GET_CART_PROMO_CODE_FEE;
    const userRaw = localStorage.getItem('user');
    let userObj: any = null;
    try {
      userObj = userRaw ? JSON.parse(userRaw) : null;
    } catch {
      userObj = null;
    }

    const userName = (userObj && userObj.userName) ? userObj.userName : '';

    const body = {
      customerNumber: customerNumber,
      promoCode: promoCode,
      cartItemsTotal: cartItemsTotal,
      languageCode: localStorage.getItem('language')

    };

    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, body).pipe(
      catchError((err, caught) => { console.log(err); console.log(caught); return empty(); }),
      map((response: any) => {
        if (response && response.data) {
          return response.data
        }
        return null;
      })
    ) as Observable<{
      userId: string
      code: string
      discount: string
      discountType: string
      savingperItem: string
      newPricePerItem: string
      applicablePromoCode: string
      freeFreight: string
      oneTime: string
      methodType: string
      cartItemCommonId: string
      quoteCommonId: string
      longItemNumber: string
      itemNumber: string
      Promofreefreight: string
      totalDiscount: string
    } | null>;
  }
 
  public getCartMinimumOrderFees(customerNumber: string) {
    const apiEndpoint = ConfigService.APIs.endPoints.CART.GET_CART_MIN_ORDER_FEE;
    const userRaw = localStorage.getItem('user');
    let userObj: any = null;
    try {
      userObj = userRaw ? JSON.parse(userRaw) : null;
    } catch {
      userObj = null;
    }

    const userName = (userObj && userObj.userName) ? userObj.userName : '';

    const body = {
      customerNumber: customerNumber
    };

    return ApiService.genericObservablePost(this.httpClient, apiEndpoint, body).pipe(
      catchError((err, caught) => { console.log(err); console.log(caught); return empty(); }),
      map((response: any) => {
        if (response && response.data) {
          return {
            minApplicableOrderFee: response.data.minApplicableOrderFee,
            orderFeesMessageEng: response.data.orderFeesMessageEng,
            orderFeesMessageFre: response.data.orderFeesMessageFre
          };
        }
        return null;
      })
    ) as Observable<{
      minApplicableOrderFee: number;
      orderFeesMessageEng: string;
      orderFeesMessageFre: string;
    } | null>;
  }
  public get listItems(): Array<any> {
    return [
      "door 1",
      1,
      "14 Clear",
      451,
      "No",
      '36"',
      '84"',
      350,
      "RHOS (LHR)",
      "Offset Pivot",
      "MS 1850",
    ];
  }

}
